import { useCallback } from "react";
import {
  openBlankTab,
  showDocumentInTab,
} from "@amzn/atlas-chat-react-library";
import { useAppSelector } from "app/store";
import { useNotifications } from "features/notifications/hooks";
import { useLazyGetCitationQuery } from "services/atlas-chat";
import { AnswersMetadata, Citation } from "features/assistant/definitions";
import { LOG } from "utils";

/**
 * Opens the document behind an Answers citation, mirroring the standalone Answers
 * chatbot's handleCitationClick: resolve a presigned download-url via the Atlas
 * download-url endpoint (appId = current team + Atlas conversation/message/citation
 * ids carried in the message's source metadata), then show it in a tab.
 */
export const useCitationClick = () => {
  const team = useAppSelector((state) => state.audits.headerSelectedTeam);
  const { addNotification } = useNotifications();
  const [triggerGetCitation] = useLazyGetCitationQuery();

  return useCallback(
    async (citation: Citation, meta: AnswersMetadata) => {
      const conversationId = meta.conversation_id;
      const messageId = meta.message_id;
      const citationId = citation.citation_id;
      if (!conversationId || !messageId || !citationId) {
        return;
      }

      const notifyError = () =>
        addNotification({
          type: "error",
          header: "Unable to open the cited document",
          dismissible: true,
          timeout: 5000,
        });

      // Open the tab synchronously inside the click gesture so strict browsers
      // (Safari) do not treat the post-fetch navigation as a blocked popup.
      const newWindow = openBlankTab();
      try {
        const url = await triggerGetCitation({
          appId: team,
          conversationId,
          messageId,
          citationId,
        }).unwrap();

        if (url && newWindow) {
          await showDocumentInTab(newWindow, {
            url,
            page: citation.page ?? undefined,
            filename: citation.title ?? undefined,
          });
        } else {
          newWindow?.close();
          notifyError();
        }
      } catch {
        // Never log the caught error — a rejected fetch can carry the presigned
        // url / response body (PII). Log a generic marker + generic toast.
        newWindow?.close();
        LOG.error("Failed to open cited document");
        notifyError();
      }
    },
    [addNotification, team, triggerGetCitation],
  );
};
